import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';

const SECTION_ID = 'efs-cart-upsell';
const DISMISSED_STORAGE_KEY = 'efs-cart-upsell-dismissed';
const DEFAULT_DELAY_MS = 800;
/** How long to wait for another dialog (e.g. quick add closing after the add) to go away. */
const WAIT_FOR_OTHER_DIALOGS_MS = 1500;

/**
 * @typedef {Object} CartUpsellRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} content - Container the fetched recommendations are rendered into.
 */

/**
 * Cart upsell dialog (WI 121, snippets/cart-upsell-dialog.liquid).
 *
 * Listens for every successful add-to-cart on the page (product page, sticky bar,
 * quick add, product cards, product popup, …). After a short delay it loads the
 * recommendations of the added product via the Section Rendering API and opens the
 * dialog if there is at least one. Adds made from inside this dialog never trigger
 * another upsell, so popups cannot chain.
 *
 * Once the customer closes the dialog without adding anything, it is not shown again
 * for that product during the browser session.
 *
 * @extends {DialogComponent}
 */
class CartUpsellDialog extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  #listeners = new AbortController();
  /** @type {AbortController | null} */
  #fetchController = null;
  /** @type {number | undefined} */
  #showTimer;
  /** Product whose add-to-cart opened the dialog. */
  #sourceProductId = '';
  #hasAdded = false;
  /** @type {HTMLElement | null} */
  #returnFocusTo = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#listeners;
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#fetchController?.abort();
    clearTimeout(this.#showTimer);
  }

  get delay() {
    const delay = Number(this.dataset.delay);
    return Number.isFinite(delay) ? delay : DEFAULT_DELAY_MS;
  }

  /**
   * "View cart": opens the cart drawer when the theme uses one, otherwise follows the link.
   * @param {MouseEvent} event
   */
  viewCart = async (event) => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer || !('open' in cartDrawer) || typeof cartDrawer.open !== 'function') return;

    event.preventDefault();
    await this.closeDialog();
    cartDrawer.open();
  };

  /**
   * `cart:update` is shared by add and update events — only successful adds count.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data?.didError) return;

    const source = event.target;
    if (!(source instanceof HTMLElement)) return;

    // An add from inside the upsell itself: show the "added" state, never a new upsell.
    if (this.contains(source)) {
      this.#markAdded();
      return;
    }

    const productUrl = source.dataset.productUrl;
    const productId = String(event.detail.data?.productId ?? source.dataset.productId ?? '');
    if (!productUrl || !productId || this.#isDismissed(productId)) return;

    clearTimeout(this.#showTimer);
    this.#fetchController?.abort();
    this.#returnFocusTo = source.querySelector('button[type="submit"]');
    this.#showTimer = setTimeout(() => this.#show(productUrl, productId), this.delay);
  };

  /**
   * @param {string} productUrl
   * @param {string} productId
   */
  async #show(productUrl, productId) {
    if (!(await this.#waitUntilUnblocked())) return;

    this.#fetchController = new AbortController();

    try {
      // product.url is relative (and may carry ?variant=), so resolve it first.
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('sections', SECTION_ID);

      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) throw new Error(`Cart upsell: HTTP ${response.status}`);

      /** @type {Record<string, string | null>} */
      const sections = await response.json();
      const html = sections[SECTION_ID];
      if (!html) return;

      // The customer may have opened something else (e.g. the cart) in the meantime.
      if (this.#isBlocked()) return;

      const { content, dialog } = this.refs;
      content.innerHTML = html;
      if (!content.querySelector('.cart-upsell')) return;

      this.#sourceProductId = productId;
      this.#hasAdded = false;
      dialog.removeAttribute('data-added');
      content.scrollTop = 0;
      this.showDialog();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.#fetchController = null;
    }
  }

  /**
   * Dialogs that close after an add (quick add, product popup) are still [open] during
   * their closing animation — give them a moment instead of dropping the upsell.
   * @returns {Promise<boolean>} false if another dialog stays open (customer is busy there).
   */
  async #waitUntilUnblocked() {
    const start = performance.now();
    while (this.#isBlocked()) {
      if (performance.now() - start > WAIT_FOR_OTHER_DIALOGS_MS) return false;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return true;
  }

  /** Never stack the upsell on top of another open dialog (quick add, cart drawer, search …). */
  #isBlocked() {
    const { dialog } = this.refs;
    if (dialog.open) return true;

    for (const openDialog of document.querySelectorAll('dialog[open]')) {
      if (openDialog !== dialog) return true;
    }
    return false;
  }

  #markAdded() {
    this.#hasAdded = true;
    this.refs.dialog.setAttribute('data-added', '');
  }

  #handleClose = () => {
    if (!this.#hasAdded && this.#sourceProductId) this.#rememberDismissed(this.#sourceProductId);

    // Return focus to the add-to-cart button that started it all — if it is still on
    // screen (a quick-add modal, for example, is gone by now).
    const target = this.#returnFocusTo;
    this.#returnFocusTo = null;
    if (!target?.isConnected) return;
    // checkVisibility() is missing in Safari < 17.4.
    const isVisible = typeof target.checkVisibility === 'function' ? target.checkVisibility() : target.offsetParent !== null;
    if (isVisible) target.focus({ preventScroll: true });
  };

  /**
   * Keeps image and links of an entry in sync with the selected variant. Price, button
   * and variant ID are updated by product-price / product-form-component themselves.
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const { html, productId } = event.detail.data;
    const item = this.querySelector(`.cart-upsell-item[data-product-id="${productId}"]`);
    const newItem = html?.querySelector('.cart-upsell-item');
    if (!item || !newItem) return;

    const media = item.querySelector('.cart-upsell-item__media');
    const newMedia = newItem.querySelector('.cart-upsell-item__media');
    if (media && newMedia) media.replaceWith(newMedia);

    const newHref = newItem.querySelector('[data-cart-upsell-link]')?.getAttribute('href');
    if (!newHref) return;
    for (const link of item.querySelectorAll('[data-cart-upsell-link]')) {
      link.setAttribute('href', newHref);
    }
  };

  /** @param {string} productId */
  #isDismissed(productId) {
    return this.#readDismissed().includes(productId);
  }

  /** @param {string} productId */
  #rememberDismissed(productId) {
    const dismissed = this.#readDismissed();
    if (dismissed.includes(productId)) return;

    try {
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed, productId]));
    } catch {
      // Storage unavailable (private mode, blocked) — the upsell may then show again.
    }
  }

  /** @returns {string[]} */
  #readDismissed() {
    try {
      const value = JSON.parse(sessionStorage.getItem(DISMISSED_STORAGE_KEY) ?? '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }
}

if (!customElements.get('cart-upsell-dialog')) {
  customElements.define('cart-upsell-dialog', CartUpsellDialog);
}
